import { faArrowsRotate } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { type ColumnDef, flexRender, type Table } from "@tanstack/react-table";
import type { ReactNode } from "react";
import { CustomCheckbox } from "../../ui/CustomCheckbox";
import { TableFilter, TableSort } from "./TableHeader";

type WithId = { id: string | number };

export interface CompactTableUIProps<T extends WithId> {
    table: Table<T>;
    dataName: string;
    columns: ColumnDef<T, any>[];
    loadingData: boolean;
    itemCount: number;
    onClickRow?: (id: T["id"], originalRow?: T) => void;
    showPagination?: boolean;
    extraFooterActions?: {
        label: string;
        buttonContent: ReactNode;
        // Omit for content that brings its own interactive element (a dropdown
        // trigger, say) - it is then rendered as-is, without a wrapper button.
        onClick?: () => void;
    }[];
    highlightRowIds?: string[];
    onHoverRow?: (id?: T["id"], originalRow?: T) => void;
}

/**
 * Compact variant of the data table UI with minimal spacing.
 */
export const CompactTableUI = <T extends WithId>(
    props: CompactTableUIProps<T>,
) => {
    const {
        table,
        loadingData,
        itemCount,
        onClickRow,
        showPagination = true,
    } = props;

    return (
        <div className="flex flex-col text-sm h-full w-full overflow-hidden">
            <div className="flex-1 min-h-0 overflow-auto">
                <table className="table-auto w-full">
                    <thead className="bg-ae-gray-50 text-left text-xs font-semibold text-ae-blue-900 border-b">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {table.options.enableRowSelection && (
                                    <th className="px-2 border-r border-gray-200 last:border-r-0">
                                        <CustomCheckbox
                                            size="small"
                                            checked={
                                                table.getIsAllRowsSelected()
                                                    ? "yes"
                                                    : table.getIsSomeRowsSelected()
                                                      ? "partial"
                                                      : "no"
                                            }
                                            onClick={() =>
                                                table.toggleAllRowsSelected(
                                                    !table.getIsAllRowsSelected(),
                                                )
                                            }
                                        />
                                    </th>
                                )}
                                {headerGroup.headers.map((header) => (
                                    <th
                                        key={header.id}
                                        className="px-2 py-1 hover:bg-neutral-100 border-r border-gray-200 last:border-r-0"
                                        style={{
                                            width:
                                                header.getSize() ===
                                                Number.MAX_SAFE_INTEGER
                                                    ? "auto"
                                                    : header.getSize(),
                                        }}
                                    >
                                        {header.isPlaceholder ? null : (
                                            <span className="flex items-center justify-between whitespace-nowrap w-full">
                                                <div className="flex items-center gap-1">
                                                    {header.column.getCanSort() && (
                                                        <TableSort
                                                            dataName={
                                                                props.dataName
                                                            }
                                                            header={header}
                                                        />
                                                    )}
                                                    {flexRender(
                                                        header.column.columnDef
                                                            .header,
                                                        header.getContext(),
                                                    )}
                                                </div>
                                                {header.column.getCanFilter() && (
                                                    <TableFilter
                                                        dataName={
                                                            props.dataName
                                                        }
                                                        header={header}
                                                    />
                                                )}
                                            </span>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>

                    {/* Compact table body */}
                    <tbody>
                        {table.getRowModel().rows.map((row) => (
                            <tr
                                key={row.id}
                                onClick={() => {
                                    if (onClickRow) {
                                        onClickRow(
                                            row.original.id,
                                            row.original,
                                        );
                                    }
                                }}
                                onMouseEnter={() =>
                                    props.onHoverRow?.(
                                        row.original.id,
                                        row.original,
                                    )
                                }
                                onMouseLeave={() => props.onHoverRow?.()}
                                className={`
                                    border-b border-b-ae-neutral-100 h-8 group/tableRow
                                    ${props.highlightRowIds?.includes(row.original.id as string) ? "bg-ae-neutral-100" : "hover:bg-ae-neutral-100"}
                                    ${onClickRow ? "hover:cursor-pointer" : ""}
                                `}
                            >
                                {table.options.enableRowSelection && (
                                    <td
                                        className="px-2 text-sm font-normal text-ae-blue-900"
                                        style={{ width: 10 }}
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyUp={(e) => e.stopPropagation()}
                                    >
                                        <CustomCheckbox
                                            size="small"
                                            checked={
                                                row.getIsSelected()
                                                    ? "yes"
                                                    : "no"
                                            }
                                            onClick={(_value, event) =>
                                                row.getToggleSelectedHandler()(
                                                    event,
                                                )
                                            }
                                        />
                                    </td>
                                )}
                                {row.getVisibleCells().map((cell) => (
                                    <td
                                        key={cell.id}
                                        className="py-1 px-2 text-xs font-normal text-ae-blue-900"
                                        style={{
                                            width:
                                                cell.column.getSize() ===
                                                Number.MAX_SAFE_INTEGER
                                                    ? "auto"
                                                    : cell.column.getSize(),
                                            maxWidth: cell.column.getSize(),
                                        }}
                                    >
                                        {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext(),
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Compact pagination footer */}
            {showPagination && (
                <div className="flex-shrink-0 bg-white border-t border-slate-200 flex items-center justify-between w-full text-xs font-medium px-2 py-1.5 text-ae-blue-900">
                    <div className="flex items-center gap-1">
                        <div className="text-xs text-gray-600">
                            {itemCount} {itemCount === 1 ? "item" : "items"}.
                        </div>
                        {props.extraFooterActions?.map((action) =>
                            action.onClick ? (
                                <button
                                    key={action.label}
                                    type="button"
                                    title={action.label}
                                    className={`py-0.5 px-1 rounded flex text-ae-blue-500 items-center gap-1 hover:bg-ae-blue-100 hover:text-ae-blue-600`}
                                    onClick={action.onClick}
                                >
                                    {action.buttonContent}
                                </button>
                            ) : (
                                <div key={action.label}>
                                    {action.buttonContent}
                                </div>
                            ),
                        )}
                    </div>

                    {loadingData && (
                        <div className="flex items-center gap-1.5 text-xs">
                            <FontAwesomeIcon
                                icon={faArrowsRotate}
                                className="w-3 animate-spin"
                            />
                        </div>
                    )}

                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            className={`p-0.5 rounded ${
                                table.getCanPreviousPage()
                                    ? "hover:bg-ae-blue-100 hover:text-ae-blue-600"
                                    : "text-neutral-300 cursor-not-allowed"
                            }`}
                            onClick={() => table.previousPage()}
                            disabled={!table.getCanPreviousPage()}
                        >
                            <ChevronLeftIcon className="h-4" />
                        </button>

                        <span className="text-xs">
                            Page {table.getState().pagination.pageIndex + 1} of{" "}
                            {table.getPageCount()}
                        </span>

                        <button
                            type="button"
                            className={`p-0.5 rounded ${
                                table.getCanNextPage()
                                    ? "hover:bg-ae-blue-100 hover:text-ae-blue-600"
                                    : "text-neutral-300 cursor-not-allowed"
                            }`}
                            onClick={() => table.nextPage()}
                            disabled={!table.getCanNextPage()}
                        >
                            <ChevronRightIcon className="h-4" />
                        </button>

                        <select
                            value={table.getState().pagination.pageSize}
                            onChange={(e) => {
                                table.setPageSize(Number(e.target.value));
                                table.setPageIndex(0);
                            }}
                            className="w-14 text-xs rounded border-neutral-300 py-0.5 px-2"
                        >
                            {[5, 10, 20].map((pageSize) => (
                                <option key={pageSize} value={pageSize}>
                                    {pageSize}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}
        </div>
    );
};
